import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Calendar, MapPin, Globe, Radar, Filter, ChevronDown, CheckSquare, Square,
  X, Users, Mail, AlertTriangle, Loader2, ExternalLink, Sparkles, StickyNote, Pencil,
  CalendarClock,
} from 'lucide-react';
import { eventsApi } from '../lib/api';
import HowDoesThisWorkPill from '../components/HowDoesThisWorkPill';
import { HelpButton } from '../components/HelpPanel';

const EVENT_TYPES = [
  'Conference', 'Summit', 'Festival', 'Symposium', 'Workshop', 'Webinar',
  'Networking Event', 'Pitch Event', 'Investor Forum', 'Award/Recognition',
  'Expo/Trade Show', 'Public Engagement', 'Policy Forum', 'Screening', 'Market',
  'Mastermind', 'CEO Roundtable', 'Accelerator Demo Day', 'Funder Convening',
  'Speaking Opportunity', 'Other',
];

// "Other" is a catch-all bucket, not a discovery category — not independently scannable.
const SCANNABLE_TYPES = EVENT_TYPES.filter((t) => t !== 'Other');

const REGIONS = ['Global', 'North America', 'Europe', 'United Kingdom', 'Latin America', 'Asia-Pacific', 'Africa'];

const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-');
const SLUG_TO_TYPE = Object.fromEntries(EVENT_TYPES.map((t) => [slug(t), t]));

const timeAgo = (iso) => {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};

const formatApiError = (err) => err?.response?.data?.detail || err?.message || 'Something went wrong';

const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function DateBox({ dateStr, tbd, recurring }) {
  if (tbd || !dateStr) {
    return (
      <div className={`flex flex-col items-center justify-center w-16 h-16 rounded-lg shrink-0 ${recurring ? 'bg-[#1A1330] border border-purple-500/40' : 'bg-[#0A1628] border border-[#1A2F4C]'}`} data-testid="event-date-box">
        <span className="text-[10px] text-[#A3B5C7]/70 uppercase tracking-wide">{recurring ? 'Next' : 'Date'}</span>
        <span className={`text-xs font-bold ${recurring ? 'text-purple-300' : 'text-amber-400'}`}>{recurring ? 'EXP.' : 'TBD'}</span>
      </div>
    );
  }
  const d = new Date(dateStr);
  return (
    <div className="flex flex-col items-center justify-center w-16 h-16 rounded-lg bg-[#00D4C8]/10 border border-[#00D4C8]/30 shrink-0" data-testid="event-date-box">
      <span className="text-[10px] text-[#00D4C8]/80 uppercase tracking-wide">{MONTHS_SHORT[d.getUTCMonth()]}</span>
      <span className="text-2xl font-bold text-[#00D4C8] leading-none">{String(d.getUTCDate()).padStart(2, '0')}</span>
      <span className="text-[9px] text-[#A3B5C7]/60">{d.getUTCFullYear()}</span>
    </div>
  );
}

function dateRange(e) {
  const fmtDate = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  };
  if (e.recurring_expected) {
    return `Next edition expected — date unconfirmed${e.last_edition_date ? `. Last edition: ${fmtDate(e.last_edition_date)}` : ''}`;
  }
  if (e.date_tbd) return 'Date TBD — confirm with organiser';
  const start = fmtDate(e.event_start_date);
  const end = fmtDate(e.event_end_date);
  return end && end !== start ? `${start} – ${end}` : start;
}

function EventCard({ event, onOutreach }) {
  const [showTargets, setShowTargets] = useState(false);
  const [targets, setTargets] = useState(null);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [notes, setNotes] = useState(event.operator_notes || '');
  const [savedNotes, setSavedNotes] = useState(event.operator_notes || '');
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [dateInput, setDateInput] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [localEvent, setLocalEvent] = useState(event);

  const saveDate = async () => {
    if (!dateInput) return;
    setSavingDate(true);
    try {
      const res = await eventsApi.overrideDate(localEvent.id, dateInput);
      setLocalEvent({ ...localEvent, event_start_date: res.data.event_start_date, date_tbd: false, recurring_expected: false });
      toast.success('Date confirmed — card updated');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingDate(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await eventsApi.saveNotes(event.id, notes);
      setSavedNotes(notes);
      setEditingNotes(false);
      toast.success('Note saved');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNotes(false);
    }
  };

  const handleShowTargets = async () => {
    const next = !showTargets;
    setShowTargets(next);
    if (next && targets === null) {
      setLoadingTargets(true);
      try {
        const res = await eventsApi.likelyTargets(event.id);
        setTargets(res.data?.likely_targets || []);
      } catch (err) {
        toast.error(formatApiError(err));
        setTargets([]);
      } finally {
        setLoadingTargets(false);
      }
    }
  };

  const confColor = { high: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10', medium: 'text-amber-400 border-amber-500/30 bg-amber-500/10', low: 'text-[#A3B5C7] border-[#1A2F4C] bg-[#0A1628]' };

  return (
    <div className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl p-5 hover:border-[#00D4C8]/30 transition-colors" data-testid={`event-card-${event.id}`}>
      <div className="flex gap-4">
        <DateBox dateStr={localEvent.event_start_date} tbd={localEvent.date_tbd} recurring={localEvent.recurring_expected} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <h3 className="text-base font-semibold text-white" data-testid="event-name">{event.name}</h3>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00D4C8]/10 text-[#00D4C8] border border-[#00D4C8]/20">{event.event_type}</span>
              {localEvent.recurring_expected && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30 flex items-center gap-1" data-testid="event-recurring-badge"><CalendarClock className="w-3 h-3" /> NEXT EDITION EXPECTED</span>
              )}
              {event.ai_discovered && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI DISCOVERED</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4 mt-2 text-xs text-[#A3B5C7] flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {dateRange(localEvent)}</span>
            {event.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {event.location}</span>}
            {event.website && (
              <a href={event.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#00D4C8] hover:underline" data-testid="event-website-link">
                <Globe className="w-3.5 h-3.5" /> Website <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {(localEvent.recurring_expected || localEvent.date_tbd) && (
            <div className="flex items-center gap-2 mt-2" data-testid="event-date-override">
              <span className="text-[10px] text-[#A3B5C7]/70">Confirm date:</span>
              <input type="date" value={dateInput} onChange={(e) => setDateInput(e.target.value)}
                className="bg-[#0A1628] border border-[#1A2F4C] rounded px-2 py-1 text-[11px] text-white focus:border-[#00D4C8] outline-none"
                data-testid="event-date-input" />
              <button onClick={saveDate} disabled={!dateInput || savingDate}
                className="text-[10px] px-2 py-1 rounded bg-[#00D4C8]/15 text-[#00D4C8] border border-[#00D4C8]/30 hover:bg-[#00D4C8]/25 disabled:opacity-40"
                data-testid="event-date-save">{savingDate ? 'Saving…' : 'Set date'}</button>
            </div>
          )}

          {event.description && <p className="text-sm text-[#D4E2F0] mt-2 leading-relaxed">{event.description}</p>}

          {(event.submission_deadline || event.registration_deadline) && (
            <div className="flex flex-col gap-1 mt-2">
              {event.submission_deadline && (
                <span className="text-xs flex items-center gap-1.5 text-[#A3B5C7]">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Submission deadline: <span className="text-white">{event.submission_deadline}</span>
                  {event.submission_deadline_closing_soon && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30 font-bold">CLOSING SOON</span>}
                </span>
              )}
              {event.registration_deadline && (
                <span className="text-xs flex items-center gap-1.5 text-[#A3B5C7]">
                  Registration deadline: <span className="text-white">{event.registration_deadline}</span>
                  {event.registration_deadline_closing_soon && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/30 font-bold">CLOSING SOON</span>}
                </span>
              )}
            </div>
          )}

          {(event.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {event.tags.slice(0, 6).map((t) => (
                <span key={t} className="px-1.5 py-0.5 text-[10px] bg-[#00D4C8]/5 text-[#00D4C8]/60 rounded">{t}</span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mt-3">
            <button onClick={handleShowTargets} className="text-xs px-3 py-1.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#A3B5C7] hover:text-white hover:border-[#00D4C8]/30 transition-colors flex items-center gap-1.5" data-testid={`show-targets-btn-${event.id}`}>
              <Users className="w-3.5 h-3.5" /> Show Targets <ChevronDown className={`w-3 h-3 transition-transform ${showTargets ? 'rotate-180' : ''}`} />
            </button>
            <button onClick={() => onOutreach(event)} className="text-xs px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 border border-[#00D4C8]/30 text-[#00D4C8] hover:bg-[#00D4C8]/20 transition-colors flex items-center gap-1.5" data-testid={`pre-event-outreach-btn-${event.id}`}>
              <Mail className="w-3.5 h-3.5" /> Pre-event Outreach
            </button>
          </div>

          {/* Operator notes — manual, never AI-generated or overwritten on re-scan */}
          <div className="mt-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C] p-2.5" data-testid={`event-notes-${event.id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#A3B5C7]/70 flex items-center gap-1"><StickyNote className="w-3 h-3" /> My notes</span>
              {!editingNotes && (
                <button onClick={() => setEditingNotes(true)} className="text-[10px] text-[#00D4C8] hover:underline flex items-center gap-1" data-testid={`edit-notes-btn-${event.id}`}>
                  <Pencil className="w-3 h-3" /> {notes ? 'Edit' : 'Add note'}
                </button>
              )}
            </div>
            {editingNotes ? (
              <div>
                <textarea
                  defaultValue={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. dinner with Adam on the 21st"
                  className="w-full bg-[#0E1C33] border border-[#1A2F4C] rounded-md p-2 text-sm text-[#D4E2F0] focus:border-[#00D4C8]/40 outline-none resize-y"
                  data-testid={`notes-textarea-${event.id}`}
                />
                <div className="flex justify-end gap-2 mt-1.5">
                  <button onClick={() => { setNotes(savedNotes); setEditingNotes(false); }} className="text-[10px] px-2 py-1 rounded text-[#A3B5C7] hover:text-white" data-testid={`notes-cancel-btn-${event.id}`}>Cancel</button>
                  <button onClick={saveNotes} disabled={savingNotes} className="text-[10px] px-2.5 py-1 rounded bg-[#00D4C8]/15 border border-[#00D4C8]/30 text-[#00D4C8] hover:bg-[#00D4C8]/25 flex items-center gap-1" data-testid={`notes-save-btn-${event.id}`}>
                    {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Save
                  </button>
                </div>
              </div>
            ) : (
              <p className={`text-sm ${notes ? 'text-[#D4E2F0]' : 'text-[#A3B5C7]/50 italic'}`} data-testid={`notes-display-${event.id}`}>
                {notes || 'No notes yet.'}
              </p>
            )}
          </div>

          {showTargets && (
            <div className="mt-3 p-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C]" data-testid={`likely-targets-panel-${event.id}`}>
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#A3B5C7]/70 mb-2">Likely to attend (reasoned from profile fit — not confirmed)</p>
              {loadingTargets ? (
                <div className="flex items-center gap-2 text-xs text-[#A3B5C7]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Assessing your targets…</div>
              ) : (targets && targets.length > 0) ? (
                <div className="space-y-2">
                  {targets.map((t) => (
                    <div key={t.name} className="flex items-start gap-2" data-testid="likely-target-row">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border uppercase shrink-0 ${confColor[(t.confidence || 'low').toLowerCase()] || confColor.low}`}>{t.confidence}</span>
                      <div className="min-w-0">
                        <span className="text-sm text-white">{t.name}</span>
                        <p className="text-xs text-[#A3B5C7]">{t.rationale}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#A3B5C7]">No existing targets have a clear connection to this event.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScanModal({ open, data, queries, onBackground, onClose }) {
  const [showQueries, setShowQueries] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  if (!open) return null;
  const done = data?.searches_done || 0;
  const total = data?.total_searches || (queries || []).length || 0;
  const lines = data?.progress_lines || [];
  const complete = data?.status === 'complete';
  const errored = data?.status === 'error';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl w-full max-w-lg p-6" data-testid="event-scan-modal">
        <div className="flex items-center gap-2 mb-3">
          <Radar className={`w-5 h-5 text-[#00D4C8] ${complete || errored ? '' : 'animate-pulse'}`} />
          <h3 className="text-lg font-semibold text-white">
            {errored ? 'Scan failed' : complete ? (data?.timed_out ? 'Scan timed out — partial results' : 'Scan complete') : 'Scanning for events…'}
          </h3>
        </div>
        <div className="w-full h-2 bg-[#0A1628] rounded-full overflow-hidden mb-2">
          <div className="h-full bg-[#00D4C8] transition-all" style={{ width: total ? `${Math.round((done / total) * 100)}%` : '10%' }} />
        </div>
        <p className="text-xs text-[#A3B5C7] mb-3" data-testid="scan-progress-summary">
          {done} / {total} searches · {data?.events_found || 0} events
          {complete && (data?.events_found === 0) && ' · no relevant events found'}
        </p>

        {complete && data?.degraded && (
          <div className="mb-3 rounded-lg bg-red-500/10 border border-red-500/40 p-2.5 text-xs text-red-300" data-testid="scan-degraded-banner">
            <span className="font-medium">⚠ Degraded scan — results not reliable.</span> {data.degraded_reason}
          </div>
        )}

        {complete && data?.diagnostics?.funnel && (
          <div className="mb-3 rounded-lg bg-[#0A1628] border border-[#1A2F4C] p-2.5 text-[11px] font-mono text-[#A3B5C7]" data-testid="scan-funnel">
            {(() => { const f = data.diagnostics.funnel; return `funnel: ${f.searches} searches → ${f.raw_results} raw → ${f.corpus_sent_to_extraction} to-extraction → ${f.extracted_candidates} extracted → ${f.passed_validation} kept`; })()}
            {data.diagnostics?.search?.first_error && <div className="text-amber-400/80 mt-1">search error: {data.diagnostics.search.first_error}</div>}
            {data.diagnostics?.extraction?.first_error && <div className="text-amber-400/80 mt-1">extraction: {data.diagnostics.extraction.first_error}</div>}
          </div>
        )}

        {complete && data?.diagnostics?.audit && (
          <div className="mb-3" data-testid="scan-audit-section">
            {(() => { const a = data.diagnostics.audit; return (
              <button onClick={() => setShowAudit((v) => !v)} className="text-xs text-[#00D4C8] hover:underline flex items-center gap-1" data-testid="toggle-scan-audit">
                <ChevronDown className={`w-3 h-3 transition-transform ${showAudit ? 'rotate-180' : ''}`} /> {showAudit ? 'Hide' : 'Show'} Scan audit — {a.total_extracted} extracted · {a.total_saved} saved · {a.total_rejected} rejected
              </button>
            ); })()}
            {showAudit && (
              <div className="mt-2 max-h-72 overflow-y-auto text-[11px] font-mono bg-[#0A1628] rounded-lg p-2 border border-[#1A2F4C] space-y-1" data-testid="scan-audit-table">
                <div className="text-[#A3B5C7] pb-1 border-b border-[#1A2F4C]">
                  rejected by stage: {Object.entries(data.diagnostics.audit.rejected_by_stage || {}).map(([k, v]) => `${k}=${v}`).join('  ·  ') || 'none'}
                  {data.diagnostics.audit.db_write_error && <span className="text-red-400"> · db_write_error: {data.diagnostics.audit.db_write_error}</span>}
                </div>
                {(data.diagnostics.audit.dispositions || []).map((d, i) => (
                  <div key={i} className={`py-0.5 ${d.disposition === 'SAVED' ? 'text-emerald-300/90' : 'text-red-300/80'}`} data-testid={`audit-row-${i}`}>
                    <span className="font-semibold">{d.disposition === 'SAVED' ? '✓ SAVED' : `✗ ${d.rejection_stage}`}</span>{' '}
                    <span className="text-[#E4ECF4]">{d.title}</span>
                    <span className="text-[#5B7089]"> · date={d.parsed_date_result || d.parsed_date_raw || 'TBD'}</span>
                    {d.rejection_reason && <div className="text-[#7E93A8] pl-4">↳ {d.rejection_reason}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {(queries || []).length > 0 && (
          <div className="mb-3">
            <button onClick={() => setShowQueries((v) => !v)} className="text-xs text-[#00D4C8] hover:underline flex items-center gap-1" data-testid="toggle-scan-queries">
              <ChevronDown className={`w-3 h-3 transition-transform ${showQueries ? 'rotate-180' : ''}`} /> {showQueries ? 'Hide' : 'Show'} the {queries.length} queries being run
            </button>
            {showQueries && (
              <div className="mt-2 max-h-32 overflow-y-auto space-y-0.5 text-[11px] text-[#A3B5C7] font-mono bg-[#0A1628] rounded-lg p-2 border border-[#1A2F4C]" data-testid="scan-queries-list">
                {queries.map((q, i) => {
                  const finished = !(data?.unfinished_queries || []).includes(q);
                  return <div key={i} className={finished ? '' : 'text-amber-400/70'}>{finished ? '✓' : '…'} {q}</div>;
                })}
              </div>
            )}
          </div>
        )}

        <div className="max-h-32 overflow-y-auto space-y-1 text-xs text-[#A3B5C7] font-mono">
          {lines.slice(-6).map((l, i) => <div key={i}>{l}</div>)}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          {complete || errored ? (
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg bg-[#00D4C8]/10 border border-[#00D4C8]/30 text-[#00D4C8] hover:bg-[#00D4C8]/20" data-testid="scan-close-btn">Close</button>
          ) : (
            <button onClick={onBackground} className="text-xs px-3 py-1.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#A3B5C7] hover:text-white" data-testid="scan-background-btn">Run in background</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EventsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [scanJobId, setScanJobId] = useState(null);
  const [scanQueries, setScanQueries] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [showRegionFilter, setShowRegionFilter] = useState(false);
  const filterRef = useRef(null);
  const regionRef = useRef(null);

  const selectedSlugs = new Set((searchParams.get('event_types') || '').split(',').filter(Boolean));
  const selectedRegions = new Set((searchParams.get('regions') || '').split(',').filter(Boolean));
  const selectedTypes = [...selectedSlugs].map((s) => SLUG_TO_TYPE[s]).filter(Boolean);
  // Categories the scan will actually run (excludes the "Other" catch-all).
  const scanCategories = selectedTypes.filter((t) => t !== 'Other');
  const catScannedAt = latest?.category_scanned_at || {};

  const loadLatest = useCallback(async () => {
    try {
      const res = await eventsApi.latest();
      setLatest(res.data?.exists ? res.data : { exists: false, events: [] });
    } catch (err) {
      console.error('Events load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  // Fetch a cost estimate whenever the category selection changes.
  useEffect(() => {
    let active = true;
    eventsApi.scanEstimate(scanCategories)
      .then((res) => { if (active) setEstimate(res.data); })
      .catch(() => { if (active) setEstimate(null); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('event_types')]);

  useEffect(() => {
    const onClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
      if (regionRef.current && !regionRef.current.contains(e.target)) setShowRegionFilter(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Poll while a job is active — independent of whether the modal is open, so
  // "Run in background" genuinely keeps the scan alive and still fires the toast.
  useEffect(() => {
    if (!scanJobId) return;
    let cancelled = false;
    // Client-side terminal fallback: if the reported progress does not advance for
    // STALL_MS (worker died / backend wedged), stop polling and surface an honest
    // error + re-enable the scan button — never spin forever. The backend also
    // self-heals a dead job to status="error" after SCAN_STALE_SECONDS; whichever
    // fires first wins.
    const STALL_MS = 240000; // 4 min with no forward progress
    let lastSig = '';
    let lastProgressAt = Date.now();
    const poll = async () => {
      try {
        const res = await eventsApi.scanStatus(scanJobId);
        if (cancelled) return;
        setScanData(res.data);
        const d = res.data;
        const sig = `${d.status}|${d.searches_done}|${d.total_searches}|${(d.progress_lines || []).length}|${d.events_found}`;
        if (sig !== lastSig) { lastSig = sig; lastProgressAt = Date.now(); }
        if (d.status === 'complete') {
          setScanJobId(null);
          const n = d.events_found || 0;
          if (d.degraded) toast.error(`Scan degraded: ${d.degraded_reason || 'upstream failure'}`, { duration: 8000 });
          else if (d.timed_out) toast.warning(`Scan timed out — ${n} events found so far`);
          else if (n === 0) toast.info('Scan complete — no relevant events found');
          else toast.success(`${n} events discovered`);
          loadLatest();
        } else if (d.status === 'error') {
          setScanJobId(null);
          toast.error(d.error || 'Scan failed');
        } else if (Date.now() - lastProgressAt > STALL_MS) {
          setScanJobId(null);
          setScanData({ ...d, status: 'error', error: 'Scan stalled with no progress — the server may have restarted mid-scan. Please try again.' });
          toast.error('Scan stalled — no progress for several minutes. Please try again.', { duration: 8000 });
        }
      } catch (err) { /* keep polling */ }
    };
    const iv = setInterval(poll, 3000);
    poll();
    return () => { cancelled = true; clearInterval(iv); };
  }, [scanJobId, loadLatest]);

  const handleScan = async () => {
    try {
      const res = await eventsApi.startScan({ categories: scanCategories });
      setScanJobId(res.data.job_id);
      setScanQueries(res.data.queries || []);
      setScanData(res.data);
      setShowScanModal(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleType = (type) => {
    const s = new Set(selectedSlugs);
    const sl = slug(type);
    s.has(sl) ? s.delete(sl) : s.add(sl);
    const params = new URLSearchParams(searchParams);
    if (s.size) params.set('event_types', [...s].join(',')); else params.delete('event_types');
    setSearchParams(params);
  };

  const clearTypes = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('event_types');
    setSearchParams(params);
  };

  const toggleRegion = (region) => {
    const s = new Set(selectedRegions);
    s.has(region) ? s.delete(region) : s.add(region);
    const params = new URLSearchParams(searchParams);
    if (s.size) params.set('regions', [...s].join(',')); else params.delete('regions');
    setSearchParams(params);
  };

  const clearRegions = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('regions');
    setSearchParams(params);
  };

  const handleOutreach = (event) => {
    const ctx = `Pre-event outreach context — Event: ${event.name} (${event.event_type})\nWhen: ${dateRange(event)}\nWhere: ${event.location || 'TBD'}\n${event.website ? 'Website: ' + event.website + '\n' : ''}${event.description || ''}`;
    navigate(`/outreach?event_context=${encodeURIComponent(ctx)}`);
  };

  const allEvents = latest?.events || [];
  const events = allEvents.filter((e) => {
    const typeOk = selectedSlugs.size === 0 || selectedSlugs.has(slug(e.event_type || 'Other'));
    const regionOk = selectedRegions.size === 0 || selectedRegions.has(e.region || 'Global');
    return typeOk && regionOk;
  });
  const datedEvents = events.filter((e) => !e.date_tbd);
  const tbdEvents = events.filter((e) => e.date_tbd);

  const scanLabel = scanCategories.length > 0
    ? `Scan ${scanCategories.length} categor${scanCategories.length === 1 ? 'y' : 'ies'}`
    : 'Scan for New Events';

  const lastDiag = latest?.last_scan_diagnostics;

  return (
    <div data-testid="events-page">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white">Market Events</h1>
          <p className="text-sm text-[#A3B5C7] mt-1">Upcoming sector conferences, summits, and speaking opportunities worldwide — discovered live, sorted soonest-first.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {latest?.exists && (
            <span className="text-xs text-[#A3B5C7]" data-testid="events-last-scan">
              Last scan{typeof latest.age_days === 'number' ? ` · ${latest.age_days === 0 ? 'today' : `${latest.age_days}d ago`}` : ''} ({allEvents.length})
              {latest.stale && <span className="text-amber-400"> · stale</span>}
            </span>
          )}
          <div className="flex flex-col items-end">
            <button onClick={handleScan} disabled={!!scanJobId} className="btn-primary text-sm flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 border-emerald-500 disabled:opacity-50" data-testid="scan-events-btn">
              <Radar className="w-4 h-4" /> {scanJobId ? 'Scanning…' : scanLabel}
            </button>
            {estimate && (
              <span className="text-[10px] text-[#A3B5C7]/70 mt-1" data-testid="scan-estimate">
                ~{estimate.estimated_searches} searches{estimate.scoped ? ' (scoped)' : ' (full scan)'}
              </span>
            )}
          </div>
          <HelpButton pageId="events" />
        </div>
      </div>
      <div className="mb-4"><HowDoesThisWorkPill feature="events" /></div>

      {/* Filter row */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setShowFilter((v) => !v)}
            className={`px-4 py-2 text-sm rounded-lg border transition-colors flex items-center gap-2 ${selectedSlugs.size > 0 ? 'bg-[#00D4C8]/10 border-[#00D4C8]/30 text-[#00D4C8]' : 'bg-[#0E1C33] border-[#1A2F4C] text-[#A3B5C7] hover:text-white'}`}
            data-testid="event-category-filter-toggle"
          >
            <Filter className="w-4 h-4" strokeWidth={1.5} /> Filter by category
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#00D4C8]/20 text-[#00D4C8]">{selectedSlugs.size > 0 ? selectedSlugs.size : EVENT_TYPES.length}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilter ? 'rotate-180' : ''}`} />
          </button>
          {showFilter && (
            <div className="absolute left-0 mt-2 w-96 max-h-[26rem] overflow-y-auto bg-[#0E1C33] border border-[#1A2F4C] rounded-lg shadow-2xl z-30 p-2" data-testid="event-category-filter-panel">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#A3B5C7]/70">Event types ({EVENT_TYPES.length})</span>
                {selectedSlugs.size > 0 && <button onClick={clearTypes} className="text-xs text-[#00D4C8] hover:underline" data-testid="event-category-clear-btn">Clear all</button>}
              </div>
              <p className="px-2 pb-1 text-[10px] text-[#A3B5C7]/60">Selecting categories scopes both this filter <span className="text-[#00D4C8]/80">and the next scan</span>.</p>
              <button onClick={clearTypes} className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-[#0A1628] transition-colors" data-testid="event-category-option-all">
                {selectedSlugs.size === 0 ? <CheckSquare className="w-4 h-4 text-[#00D4C8] shrink-0" strokeWidth={1.5} /> : <Square className="w-4 h-4 text-[#A3B5C7]/50 shrink-0" strokeWidth={1.5} />}
                <span className="text-sm text-white">All categories</span>
              </button>
              {EVENT_TYPES.map((type) => (
                <button key={type} onClick={() => toggleType(type)} className="w-full flex items-center justify-between gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-[#0A1628] transition-colors" data-testid={`event-category-option-${slug(type)}`}>
                  <span className="flex items-center gap-2.5">
                    {selectedSlugs.has(slug(type)) ? <CheckSquare className="w-4 h-4 text-[#00D4C8] shrink-0" strokeWidth={1.5} /> : <Square className="w-4 h-4 text-[#A3B5C7]/50 shrink-0" strokeWidth={1.5} />}
                    <span className="text-sm text-[#D4E2F0]">{type}</span>
                  </span>
                  {type !== 'Other' && (
                    <span className="text-[9px] text-[#A3B5C7]/50 shrink-0" data-testid={`event-category-scanned-${slug(type)}`}>{timeAgo(catScannedAt[type])}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Region filter */}
        <div className="relative" ref={regionRef}>
          <button
            onClick={() => setShowRegionFilter((v) => !v)}
            className={`px-4 py-2 text-sm rounded-lg border transition-colors flex items-center gap-2 ${selectedRegions.size > 0 ? 'bg-[#00D4C8]/10 border-[#00D4C8]/30 text-[#00D4C8]' : 'bg-[#0E1C33] border-[#1A2F4C] text-[#A3B5C7] hover:text-white'}`}
            data-testid="event-region-filter-toggle"
          >
            <Globe className="w-4 h-4" strokeWidth={1.5} /> Filter by region
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#00D4C8]/20 text-[#00D4C8]">{selectedRegions.size > 0 ? selectedRegions.size : REGIONS.length}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRegionFilter ? 'rotate-180' : ''}`} />
          </button>
          {showRegionFilter && (
            <div className="absolute left-0 mt-2 w-64 bg-[#0E1C33] border border-[#1A2F4C] rounded-lg shadow-2xl z-30 p-2" data-testid="event-region-filter-panel">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#A3B5C7]/70">Regions (filter only)</span>
                {selectedRegions.size > 0 && <button onClick={clearRegions} className="text-xs text-[#00D4C8] hover:underline" data-testid="event-region-clear-btn">Clear</button>}
              </div>
              {REGIONS.map((region) => (
                <button key={region} onClick={() => toggleRegion(region)} className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-[#0A1628] transition-colors" data-testid={`event-region-option-${slug(region)}`}>
                  {selectedRegions.has(region) ? <CheckSquare className="w-4 h-4 text-[#00D4C8] shrink-0" strokeWidth={1.5} /> : <Square className="w-4 h-4 text-[#A3B5C7]/50 shrink-0" strokeWidth={1.5} />}
                  <span className="text-sm text-[#D4E2F0]">{region}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Active chips */}
      {(selectedSlugs.size > 0 || selectedRegions.size > 0) && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="event-active-chips">
          {EVENT_TYPES.filter((t) => selectedSlugs.has(slug(t))).map((type) => (
            <button key={type} onClick={() => toggleType(type)} className="px-2.5 py-1 text-xs rounded-full bg-[#00D4C8]/10 border border-[#00D4C8]/30 text-[#00D4C8] flex items-center gap-1.5 hover:bg-[#00D4C8]/20 transition-colors" data-testid={`event-active-chip-${slug(type)}`}>
              {type} <X className="w-3 h-3" />
            </button>
          ))}
          {[...selectedRegions].map((region) => (
            <button key={region} onClick={() => toggleRegion(region)} className="px-2.5 py-1 text-xs rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-300 flex items-center gap-1.5 hover:bg-purple-500/20 transition-colors" data-testid={`event-active-region-chip-${slug(region)}`}>
              <Globe className="w-3 h-3" /> {region} <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      {/* Last-scan diagnostics banner (funnel + degraded/timeout surfacing) — always
          visible when a scan has run, so the funnel is never hidden from the operator. */}
      {lastDiag && lastDiag.funnel && (
        <div className={`rounded-lg p-3 mb-4 text-xs border ${lastDiag.degraded ? 'bg-red-500/10 border-red-500/40 text-red-300' : lastDiag.timed_out ? 'bg-amber-500/10 border-amber-500/40 text-amber-300' : 'bg-[#0E1C33] border-[#1A2F4C] text-[#A3B5C7]'}`} data-testid="last-scan-diagnostics">
          <span className="font-medium">
            {lastDiag.degraded ? '⚠ Last scan degraded — results may be incomplete.'
              : lastDiag.timed_out ? '⚠ Last scan timed out — partial results shown.'
              : '✓ Last scan completed cleanly.'}
          </span>
          {lastDiag.degraded_reason && <span> {lastDiag.degraded_reason}</span>}
          <span className="block mt-1 font-mono text-[11px]">
            funnel: {lastDiag.funnel.searches} searches → {lastDiag.funnel.raw_results} raw → {lastDiag.funnel.extracted_candidates} extracted → {lastDiag.funnel.passed_validation} kept
            {typeof lastDiag?.validation?.rejected_past_or_imminent === 'number' && ` · ${lastDiag.validation.rejected_past_or_imminent} past/imminent rejected`}
          </span>
          {(lastDiag?.search?.first_error || lastDiag?.extraction?.first_error) && (
            <span className="block mt-1 font-mono text-[11px] text-amber-400/80">
              {lastDiag.search.first_error ? `search: ${lastDiag.search.first_error}` : `extraction: ${lastDiag.extraction.first_error}`}
            </span>
          )}
        </div>
      )}

      {/* Events list */}
      {loading ? (
        <div className="flex items-center gap-2 text-[#A3B5C7] py-16 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading events…</div>
      ) : events.length === 0 ? (
        <div className="text-center py-16" data-testid="events-empty">
          <Calendar className="w-10 h-10 text-[#1A2F4C] mx-auto mb-3" />
          <p className="text-[#A3B5C7] mb-1">
            {allEvents.length === 0 ? 'No upcoming events found yet.' : 'No events match the selected filters.'}
          </p>
          <p className="text-xs text-[#A3B5C7]/60 mb-4">
            {allEvents.length === 0 ? 'Run a scan to discover events matching your profile, or adjust your profile tags.' : 'Clear the filters to see all discovered events.'}
          </p>
          {allEvents.length === 0 && (
            <button onClick={handleScan} disabled={!!scanJobId} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="empty-scan-btn"><Radar className="w-4 h-4" /> {scanLabel}</button>
          )}
        </div>
      ) : (
        <div className="space-y-8" data-testid="events-list">
          {datedEvents.length > 0 && (
            <div data-testid="events-dated-section">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-[#00D4C8]" />
                <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-[#A3B5C7]">
                  Confirmed dates <span className="text-[#00D4C8]">({datedEvents.length})</span>
                </h2>
              </div>
              <div className="space-y-3">
                {datedEvents.map((e) => <EventCard key={e.id} event={e} onOutreach={handleOutreach} />)}
              </div>
            </div>
          )}

          {tbdEvents.length > 0 && (
            <div data-testid="events-tbd-section">
              <div className="flex items-center gap-2 mb-1">
                <CalendarClock className="w-4 h-4 text-amber-400" />
                <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-amber-300">
                  Needs date confirmation <span className="text-amber-400">({tbdEvents.length})</span>
                </h2>
              </div>
              <p className="text-xs text-[#A3B5C7]/70 mb-3 pl-6">
                Discovered without a parseable date — a human needs to confirm the date with the organiser before these are fully actionable.
              </p>
              <div className="space-y-3">
                {tbdEvents.map((e) => <EventCard key={e.id} event={e} onOutreach={handleOutreach} />)}
              </div>
            </div>
          )}
        </div>
      )}

      <ScanModal
        open={showScanModal}
        data={scanData}
        queries={scanQueries}
        onBackground={() => setShowScanModal(false)}
        onClose={() => setShowScanModal(false)}
      />
    </div>
  );
}
